import { Settings } from "../../../Settings";
import Character from "../../character/Character";
import { CharacterAction } from "../../character/CharacterAction";
import GridTile from "../../grid/GridTile";
import Vector from "../../math/Vector";

/**
 * @abstract
 */
export default class PathPlan {
    /**
     * @param {GridTile} startTile 
     * @param {GridTile} endTile 
     * @param {number} pathSteps
     */
    constructor(startTile, endTile, pathSteps) {
        /**
         * @constant
         * @type {GridTile}
         */
        this.startTile = startTile;
        /**
         * @constant
         * @type {GridTile}
         */
        this.endTile = endTile;

        /**
         * @constant
         * @type {number}
         */
        this.pathSteps = pathSteps;

        /**
         * @type {number}
         */
        this.fallSpeedMultiplier = 1;
    }

    /**
     * @return {boolean} True if this plan requires that the character be standing on top of a solid tile when it starts.
     */
    isSolidFloorRequiredAtStart() {
        return true;
    }

    /**
     * @abstract
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {boolean} turnStart True if this is the start of a turn.
     * @return {boolean} True when the step is completed.
     */
    executePlan(character, frameTimeRatio, turnStart) {}

    /**
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {number} tileCenterX 
     * @returns {boolean} True when the center has been reached.
     */
    walkLeftToCenterX(character, frameTimeRatio, tileCenterX) {
        const characterCenterX = character.position.worldCoordinateCenter.x;
        const distanceToEnd = Math.abs(characterCenterX - tileCenterX);
        if (distanceToEnd === 0) {
            return true;
        }

        character.setCharacterAction(CharacterAction.WALK);
        character.position.facingLeft = true;

        const walkDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        // If we are close enough to the center, then we are done.
        if (distanceToEnd < walkDistancePerFrame) {
            character.position.moveX(-distanceToEnd);
            return true; // We are done
        }
        else {
            character.position.moveX(-walkDistancePerFrame);
            return false; // We are still waking towards the center
        }
    }
    
    /**
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {number} tileCenterX 
     * @returns {boolean} True when the center has been reached.
     */
    walkRightToCenterX(character, frameTimeRatio, tileCenterX) {
        const characterCenterX = character.position.worldCoordinateCenter.x;
        const distanceToEnd = Math.abs(tileCenterX - characterCenterX);
        if (distanceToEnd === 0) {
            return true;
        }

        character.setCharacterAction(CharacterAction.WALK);
        character.position.facingLeft = false;

        const walkDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        // If we are close enough to the center, then we are done.
        if (distanceToEnd < walkDistancePerFrame) {
            character.position.moveX(distanceToEnd);
            return true; // We are done
        }
        else {
            character.position.moveX(walkDistancePerFrame);
            return false; // We are still waking towards the center
        }
    }


    /**
     * Jump from a lower position to a higher position that is directly above.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} endTile
     * @returns {boolean} True when done
     */
    jumpUp(character, frameTimeRatio, endTile) {
        
        /////////////////////////////////////////////
        // Jump up
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.JUMP);

        const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

        const distanceToJumpTop = character.position.worldCoordinateOrigin.y - endTile.origin.y;

        // If we are close enough to the end of our jump, we are done.
        if (distanceToJumpTop < yDistancePerFrame) {
            // Instead of adding the delta, let's just directly set the y-coordinate
            character.position.setY(endTile.origin.y);
            return true;
        }
        else {
            character.position.moveY(-yDistancePerFrame);
            return false;
        }
    }


    /**
     * Jump from a lower position to a higher position that is to the left.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     */
    jumpUpAndLeft(character, frameTimeRatio, jumpCoordinate, landCoordinate) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (jumpCoordinateX - landCoordinateX) / 2; 
        const midAirJumpCenterX = landCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        //const jumpHorizontalDistance = Math.abs(jumpCoordinateX - landCoordinateX);
        const jumpVerticalDistance = (jumpCoordinate.y - landCoordinate.y) + Settings.tile.halfSize; // y increases going down, and start is below end.

        const midAirJumpY = jumpCoordinate.y - jumpVerticalDistance;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = true;

        /////////////////////////////////////////////
        // Jump up & Left
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x > midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = character.position.worldCoordinateCenter.x - midAirJumpCenterX;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(-xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(-xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Left
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = character.position.worldCoordinateCenter.x - landCoordinateX;
        const distanceToLandY = this.endTile.origin.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);
            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
    }

    /**
     * Jump from a lower position to a higher position that is to the right.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     */
    jumpUpAndRight(character, frameTimeRatio, jumpCoordinate, landCoordinate) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (landCoordinateX - jumpCoordinateX) / 2;
        const midAirJumpCenterX = jumpCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        const jumpVerticalDistance = (jumpCoordinate.y - landCoordinate.y) + Settings.tile.halfSize; // y increases going down, and start is below end.

        const midAirJumpY = jumpCoordinate.y - jumpVerticalDistance;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = false;

        /////////////////////////////////////////////
        // Jump up & Right
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x < midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = midAirJumpCenterX - character.position.worldCoordinateCenter.x;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Right
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = landCoordinateX - character.position.worldCoordinateCenter.x;
        const distanceToLandY = landCoordinate.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
    }

    /**
     * Jump from a higher position to a lower position that is to the left.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     */
    jumpDownAndLeft(character, frameTimeRatio, jumpCoordinate, landCoordinate) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (jumpCoordinateX - landCoordinateX) / 2;
        const midAirJumpCenterX = landCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        const jumpVerticalDistance = Settings.tile.halfSize; // y increases going down

        const midAirJumpY = jumpCoordinate.y - jumpVerticalDistance;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = true;

        /////////////////////////////////////////////
        // Jump up & Left
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x > midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = character.position.worldCoordinateCenter.x - midAirJumpCenterX;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(-xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(-xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Left
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = character.position.worldCoordinateCenter.x - landCoordinateX;
        const distanceToLandY = this.endTile.origin.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);
            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
    }


    /**
     * Jump from a higher position to a lower position that is to the right.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     */
    jumpDownAndRight(character, frameTimeRatio, jumpCoordinate, landCoordinate) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (landCoordinateX - jumpCoordinateX) / 2;
        const midAirJumpCenterX = jumpCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        const jumpVerticalDistance = Settings.tile.halfSize; // y increases going down

        const midAirJumpY = jumpCoordinate.y - jumpVerticalDistance;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = false;

        /////////////////////////////////////////////
        // Jump up & Right
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x < midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = midAirJumpCenterX - character.position.worldCoordinateCenter.x;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Right
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);
        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = landCoordinateX - character.position.worldCoordinateCenter.x;
        const distanceToLandY = landCoordinate.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
    }

    /**
     * Jump left from one tile to another, both at the same height.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     * @param {number} jumpHeight
     */
    jumpLeft(character, frameTimeRatio, jumpCoordinate, landCoordinate, jumpHeight) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (jumpCoordinateX - landCoordinateX) / 2;
        const midAirJumpCenterX = landCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        const midAirJumpY = jumpCoordinate.y - jumpHeight;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = true;

        /////////////////////////////////////////////
        // Jump up & Left
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x > midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = character.position.worldCoordinateCenter.x - midAirJumpCenterX;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(-xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(-xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Left
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = character.position.worldCoordinateCenter.x - landCoordinateX;
        const distanceToLandY = this.endTile.origin.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);

            character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
        }
    }

    /**
     * Jump right from one tile to another, both at the same height.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} jumpCoordinate 
     * @param {Vector} landCoordinate 
     * @param {number} jumpHeight
     */
    jumpRight(character, frameTimeRatio, jumpCoordinate, landCoordinate, jumpHeight) {
        
        const jumpCoordinateX = jumpCoordinate.x;
        const landCoordinateX = landCoordinate.x;

        const halfJumpDistance = (landCoordinateX - jumpCoordinateX) / 2;
        const midAirJumpCenterX = jumpCoordinateX + halfJumpDistance; // mid-air center x-coordinate

        const midAirJumpY = jumpCoordinate.y - jumpHeight;

        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        character.position.facingLeft = false;

        /////////////////////////////////////////////
        // Jump up & Right
        /////////////////////////////////////////////

        if (character.position.worldCoordinateCenter.x < midAirJumpCenterX) {
            character.setCharacterAction(CharacterAction.JUMP);

            const yDistancePerFrame = this.getJumpDistanceThisFrame(frameTimeRatio);

            // We want to reach the mid air jump center x at the same time we reach the top of our jump.
            const distanceToJumpCenter = midAirJumpCenterX - character.position.worldCoordinateCenter.x;
            const distanceToJumpTop = character.position.worldCoordinateOrigin.y - midAirJumpY;

            if (distanceToJumpCenter > distanceToJumpTop) {
                // max x-coordinate motion, limit y-coordiante motion
                const yRatio = this.calcRatio(distanceToJumpTop, distanceToJumpCenter); // this will be less than 1.
                const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToJumpTop);
                const xMotion = Math.min(xDistancePerFrame, distanceToJumpCenter);
                character.position.moveXY(xMotion, -yMotion);
            }
            else {
                // max y-coordinate motion, limit x-coordinate motion
                const xRatio = this.calcRatio(distanceToJumpCenter, distanceToJumpTop); // this will be less than 1.
                const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToJumpCenter);
                const yMotion = Math.min(yDistancePerFrame, distanceToJumpTop);
                character.position.moveXY(xMotion, -yMotion); // moving up requires a negative value
            }

            return;
        }
        
        /////////////////////////////////////////////
        // Fall & Right
        /////////////////////////////////////////////
        
        character.setCharacterAction(CharacterAction.FALL);

        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);

        // We want to reach the land coordiante x at the same time we reach the land coordinate y
        const distanceToLandX = landCoordinateX - character.position.worldCoordinateCenter.x;
        const distanceToLandY = landCoordinate.y - character.position.worldCoordinateOrigin.y;

        // Avoid NaN because are about to calc the ratio between these two
        if (distanceToLandX === 0 && distanceToLandY === 0) {
            return;
        }

        if (distanceToLandX > distanceToLandY) {
            // max x-coordinate motion, limit y-coordiante motion
            const yRatio = this.calcRatio(distanceToLandY, distanceToLandX); // this will be less than 1.
            const yMotion = Math.min(yRatio * yDistancePerFrame, distanceToLandY);
            const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
        else {
            // max y-coordinate motion, limit x-coordinate motion
            const xRatio = this.calcRatio(distanceToLandX, distanceToLandY); // this will be less than 1.
            const xMotion = Math.min(xRatio * xDistancePerFrame, distanceToLandX);
            const yMotion = Math.min(yDistancePerFrame, distanceToLandY);
            character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
        }
    }

    /**
     * Fall from a higher position to a lower position that is to the left.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} landCoordinate 
     */
    fallDownAndLeft(character, frameTimeRatio, landCoordinate) {

        character.position.facingLeft = true;
        
        /////////////////////////////////////////////
        // Fall & Left
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);
        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        const distanceToLandX = character.position.worldCoordinateCenter.x - landCoordinate.x;
        const distanceToLandY = landCoordinate.y - character.position.worldCoordinateOrigin.y;

        const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
        const yMotion = Math.min(yDistancePerFrame, distanceToLandY);

        character.position.moveXY(-xMotion, yMotion); // moving down requires a positive value
    }

    /**
     * Fall from a higher position to a lower position that is to the right.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} landCoordinate 
     */
    fallDownAndRight(character, frameTimeRatio, landCoordinate) {

        character.position.facingLeft = false;
        
        /////////////////////////////////////////////
        // Fall & Right
        /////////////////////////////////////////////

        character.setCharacterAction(CharacterAction.FALL);

        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);
        const xDistancePerFrame = this.getWalkDistanceThisFrame(frameTimeRatio);

        const distanceToLandX = landCoordinate.x - character.position.worldCoordinateCenter.x;
        const distanceToLandY = landCoordinate.y - character.position.worldCoordinateOrigin.y;

        const xMotion = Math.min(xDistancePerFrame, distanceToLandX);
        const yMotion = Math.min(yDistancePerFrame, distanceToLandY);

        character.position.moveXY(xMotion, yMotion); // moving down requires a positive value
    }

    /**
     * Fall from a higher position to a lower position that is to the left.
     * @param {Character} character 
     * @param {number} frameTimeRatio 
     * @param {Vector} landCoordinate 
     * @returns {boolean} True when completed.
     */
    fallDown(character, frameTimeRatio, landCoordinate) {
        
        character.setCharacterAction(CharacterAction.FALL);
        
        const yDistancePerFrame = this.getFallDistanceThisFrame(frameTimeRatio);
        const distanceToLandY = landCoordinate.origin.y - character.position.worldCoordinateOrigin.y;

        if (yDistancePerFrame < distanceToLandY) {
            character.position.moveY(yDistancePerFrame); // moving down requires a positive value
            return false;
        }
        else {
            character.position.setY(landCoordinate.origin.y);
            return true;
        }
    }


    /**
     * @param {number} frameTimeRatio 
     * @returns {number}
     */
    getWalkDistanceThisFrame(frameTimeRatio) {
        return Settings.physics.walkRatePerFrame * frameTimeRatio;
    }

    /**
     * @param {number} frameTimeRatio 
     * @returns {number}
     */
    getFallDistanceThisFrame(frameTimeRatio) {
        return Settings.physics.gravityRatePerFrame * frameTimeRatio * this.fallSpeedMultiplier;
    }
    
    /**
     * @param {number} frameTimeRatio 
     * @returns {number}
     */
    getJumpDistanceThisFrame(frameTimeRatio) {
        return Settings.physics.jumpRatePerFrame * frameTimeRatio;
    }

    /**
     * @param {Position} position
     * @returns {number}
     */
    getDistanceAboveFloor(position) {
        // If we are hovering in the air 4 pixels above the floor, then this will return 4.
        const floorY = position.tile.origin.y;
        const characterY = position.worldCoordinateOrigin.y;

        // y increases downwards. floorY is greater than charY if it is below it.
        return floorY - characterY;
    }

    /**
     * 
     * @param {Character} character 
     * @param {GridTile} endTile 
     */
    assertEndTileReached(character, endTile) {
        if (character.position.tile !== endTile) {
            const origin = endTile.origin;
            character.position.setTileAndOriginPosition(endTile, origin.x, origin.y)
        }
        else {
            character.position.setPositionToTileOrigin();
        }
    }


    /**
     * Calculate the ratio between two numbers, avoiding 0 denominator.
     * @private
     * @param {number} numerator 
     * @param {number} denominator 
     * @returns {number}
     */
    calcRatio(numerator, denominator) {

        // Avoid 0/0 NaN stuff.
        if (numerator === denominator) {
            return 1;
        }

        if (numerator === 0) {
            return 0;
        }

        if (denominator === 0) {
            console.log('calcRatio() 0 denominator');
            return 1000; // Not sure what to do.
        }

        return numerator / denominator;
    }

    /**
     * @abstract
     * @returns {String}
     */
    toString() {
        return 'Base PathPlan toString() sub-class did not override.';
    }

}